import { isBrowser } from '@utils/isBrowser';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import React, { useEffect } from 'react';

import { useTheme } from '../../../components/Layout/Theme/useTheme';
import { ProductGateway } from '../../../types/AvantArteProduct';
import { GeneratedPrivateLink } from '../../../types/generated';
import { GiftCardCheckoutPayload } from '../../../types/GiftCard';
import {
  CheckoutGateway,
  ProductsGateway,
  ShopifyProductPurchaseDetails,
} from '../../../types/Purchasing';
import { usePrivateSales } from '../hooks/usePrivateSales';
import { PrivateSalesGateway } from '../types/PrivateLink';
import { extractSlugFromPathname } from '../utils';
import { Layout } from './layout/Layout';
import { List } from './list/List';
import { Loading } from './unlock/Loading';
import { UnlockForm } from './unlock/UnlockForm';

const Expired = dynamic(() => import('./Expired').then((mod) => mod.Expired), {
  ssr: false,
});

interface PrivateSalesProps {
  privateLink: GeneratedPrivateLink;
  privateSalesGateway: PrivateSalesGateway;
  locale: string;
  ogImageUrl: string;
  checkoutGateway: CheckoutGateway<
    ShopifyProductPurchaseDetails,
    GiftCardCheckoutPayload
  >;
  aaProductsGateway: ProductGateway;
  productsGateway: ProductsGateway<
    ShopifyProductIdentifier,
    ShopifyProductPurchaseDetails
  >;
  isExpired: boolean;
}

export const PrivateSales = ({
  aaProductsGateway,
  checkoutGateway,
  isExpired,
  locale,
  ogImageUrl,
  privateLink,
  privateSalesGateway,
  productsGateway,
}: PrivateSalesProps) => {
  useTheme('dark');

  // Need to pretend we're on https://avantarte.com for SSR
  const router = useRouter();
  const url = new URL(router.asPath, 'https://avantarte.com');
  const slug = extractSlugFromPathname(url.pathname);
  const privateSalesController = usePrivateSales({
    privateSalesGateway,
    slug,
    passwordRequired: privateLink.passwordRequired,
    isExpired,
  });

  const { locked, showLoadingScreen } = privateSalesController;

  const urlHasHash = isBrowser() && window.location.hash;

  useEffect(() => {
    if (urlHasHash) {
      // Remove hash (password) from URL
      router.replace(router.asPath.split('#')[0]);
    }
  }, [urlHasHash, router]);

  return (
    <Layout locale={locale} ogImageUrl={ogImageUrl} privateLink={privateLink}>
      {isExpired && privateLink.expiresAt ? (
        <Expired expiresAt={privateLink.expiresAt} />
      ) : null}

      {showLoadingScreen && <Loading />}

      {!showLoadingScreen && !isExpired && !locked && (
        <List
          aaProductsGateway={aaProductsGateway}
          checkoutGateway={checkoutGateway}
          privateLink={privateLink}
          privateSalesController={privateSalesController}
          productsGateway={productsGateway}
        />
      )}

      {!showLoadingScreen &&
      !isExpired &&
      locked &&
      privateLink.passwordRequired ? (
        <UnlockForm
          privateLink={privateLink}
          privateSalesController={privateSalesController}
        />
      ) : null}
    </Layout>
  );
};
